/**
 * WorkerAutoComplete - 工人姓名自动补全组件
 *
 * 功能：
 * - 实时防抖搜索（300ms），调用 /api/worker-module/workers/search
 * - 候选下拉展示「姓名（工人类型）」
 * - 选中后通过 onWorkerSelect 回调完整工人对象（含 _id、defaultPrice 等）
 * - 支持自由输入（手动输入不在系统中的姓名）
 *
 * 使用场景：
 * - /worker-manage 筛选栏实时搜索
 * - /worker-history 新增出工记录表单工人名字字段
 */
import React, { useRef, useState } from 'react';
import { AutoComplete, Spin } from 'antd';
import { WorkerService } from '@/services/workerService';

/** 搜索结果中工人对象的类型（来自 /search 接口） */
export interface WorkerSearchResult {
  _id: string;
  name: string;
  workerType: string;
  defaultPrice: number;
  phone?: string;
  isLeader: boolean;
}

export interface WorkerAutoCompleteProps {
  /** 受控文本值（工人姓名字符串） */
  value?: string;
  /** 文本变化回调，用于表单 onChange 绑定 */
  onChange?: (name: string) => void;
  /** 从候选列表选中工人时的回调，携带完整工人对象 */
  onWorkerSelect?: (worker: WorkerSearchResult) => void;
  /** 清空时的回调 */
  onClear?: () => void;
  placeholder?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

/** 工人类型中文映射 */
const WORKER_TYPE_LABEL: Record<string, string> = {
  male: '男工',
  female: '女工',
  technician: '技工',
  manager: '管理',
};

const WorkerAutoComplete: React.FC<WorkerAutoCompleteProps> = ({
  value,
  onChange,
  onWorkerSelect,
  onClear,
  placeholder = '请输入工人姓名',
  style,
  disabled = false,
}) => {
  const [options, setOptions] = useState<{ value: string; label: string; worker: WorkerSearchResult }[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** 防抖搜索：输入变化后 300ms 发起请求 */
  const handleSearch = (keyword: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!keyword || !keyword.trim()) {
      setOptions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await WorkerService.searchWorkers(keyword.trim());
        const workers: WorkerSearchResult[] = res.data || [];
        setOptions(
          workers.map((w) => ({
            value: w.name,
            label: `${w.name}（${WORKER_TYPE_LABEL[w.workerType] || w.workerType}）`,
            worker: w,
          }))
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  /** 文本变化：通知父组件 + 触发防抖搜索 */
  const handleChange = (text: string) => {
    onChange?.(text);
    handleSearch(text);
    if (!text) {
      onClear?.();
    }
  };

  /** 选中候选项：回调完整工人对象 */
  const handleSelect = (_val: string, option: any) => {
    const worker: WorkerSearchResult = option.worker;
    onWorkerSelect?.(worker);
    // 清空搜索候选（已选定，不再需要下拉）
    setOptions([]);
  };

  /** 构建 AutoComplete 的 options（需符合 antd 格式，label 用于展示，value 用于填充） */
  const autoCompleteOptions = options.map((opt) => ({
    value: opt.value,
    label: opt.label,
    worker: opt.worker,
  }));

  return (
    <AutoComplete
      value={value}
      options={autoCompleteOptions}
      onSearch={handleSearch}
      onChange={handleChange}
      onSelect={handleSelect}
      onClear={() => {
        setOptions([]);
        onClear?.();
      }}
      placeholder={placeholder}
      style={style}
      disabled={disabled}
      allowClear
      notFoundContent={
        loading
          ? <Spin size="small" style={{ display: 'block', textAlign: 'center', padding: '8px' }} />
          : null
      }
    />
  );
};

export default WorkerAutoComplete;
